// 가입 직후 뒤처리 쪽지 — 추천인 연결·가입 환영 알림톡·약관 동의 기록·가입 접속 정보 (2026-10-22 daniel-5b, 팀장 daniel-14 승인 15:2x)
// 왜: 인증에 성공하면 로그인 감지(onAuthStateChange)가 곧바로 user를 넣고, 로그인 화면이 뒤처리를 끝내기 전에 대시보드로 넘어갔다.
//     배포 뒤 옛 화면을 띄워 둔 손님은 대시보드 파일이 없어 '새 판 받기 자동 새로고침'(main.tsx·AppErrorBoundary)이 돌고, 진행 중이던 뒤처리가 통째로 끊겼다.
//     (10/15~10/22 1분 안에 인증한 새 회원 5,969명: 배포 뒤 1분 안에 인증한 25명 중 9명이 알림톡을 못 받음 — 나머지는 0.24%, 못 받은 23명 모두 서버에 요청 0)
// 무엇: 인증 요청 직전에 쪽지를 남기고, 단계마다 끝난 표시를 하고, 다 끝나면 지운다. 새로고침 등으로 끊기면 앱이 다시 켜질 때 남은 단계만 마저 한다.
// 서버 규칙은 그대로 따른다: 추천인 연결은 서버가 '생성 30분 안·입금 전'만 허용, 알림톡은 서버 '한 번만' 표(signup_alimtalk_sends), 접속 정보는 이미 있으면 건너뜀.
// 문서: Readme/SignupFollowUpResume.md
import type { User as SupabaseUser } from '@supabase/supabase-js';
import { COLLECTION_NOTICE, VIP_THIRD_PARTY_NOTICE, PRIVACY_POLICY_EFFECTIVE_DATE, noticeAsText } from './privacyNotice';
import { supabase } from './supabase';
import { recordTermsConsent } from './termsConsent';
import { FRESH_ACCOUNT_MS, isFreshAccount } from './freshAccount';

export type SignupStep = 'referral' | 'alimtalk' | 'consent' | 'tracking';

interface SignupFollowUpNote {
    v: 1;
    phone: string;      // 손님이 입력한 번호 그대로(알림톡 함수가 받는 값)
    phoneKey: string;   // 82로 시작하는 숫자만 — 다른 번호로 로그인한 세션에 잘못 쓰지 않게 대조
    at: number;         // 쪽지를 쓴 시각(ms)
    agreedToTerms: boolean;
    isSignUp: boolean;
    isExistingUser: boolean;
    done: Partial<Record<SignupStep, boolean>>;
}

const NOTE_KEY = 'sdl_signup_followup_v1';

// 같은 화면에서 인증이 진행 중이면 이어 하기가 끼어들지 않는다(새로고침하면 false로 돌아온다)
let verifying = false;
let resuming: Promise<void> | null = null;

const toPhoneKey = (phone?: string | null): string => {
    const digits = (phone ?? '').replace(/\D/g, '');
    if (digits.startsWith('82')) return digits;
    if (digits.startsWith('0')) return '82' + digits.substring(1);
    return digits;
};

const readNote = (): SignupFollowUpNote | null => {
    try {
        const raw = localStorage.getItem(NOTE_KEY);
        if (!raw) return null;
        const note = JSON.parse(raw) as SignupFollowUpNote;
        return note && note.v === 1 ? note : null;
    } catch {
        return null;
    }
};

const writeNote = (note: SignupFollowUpNote): void => {
    try {
        localStorage.setItem(NOTE_KEY, JSON.stringify(note));
    } catch { /* 프라이빗 모드 등 — 쪽지 없이 예전처럼 진행 */ }
};

const clearNote = (): void => {
    try {
        localStorage.removeItem(NOTE_KEY);
    } catch { /* 무시 */ }
};

/** 인증 확인을 누른 순간(로그인 화면) — 쪽지를 새로 쓰고 '진행 중'으로 둔다 */
export function beginSignupFollowUp(input: { phone: string; agreedToTerms: boolean; isSignUp: boolean; isExistingUser: boolean }): void {
    verifying = true;
    writeNote({
        v: 1,
        phone: input.phone,
        phoneKey: toPhoneKey(input.phone),
        at: Date.now(),
        agreedToTerms: input.agreedToTerms,
        isSignUp: input.isSignUp,
        isExistingUser: input.isExistingUser,
        done: {},
    });
}

/** 한 단계가 끝나면 표시 — 다시 열려도 이 단계는 건너뛴다 */
export function markSignupStepDone(step: SignupStep): void {
    const note = readNote();
    if (!note) return;
    note.done[step] = true;
    writeNote(note);
}

/** 다 끝났거나 할 일이 없을 때(차단 손님 포함) — 쪽지를 지운다 */
export function finishSignupFollowUp(): void {
    verifying = false;
    clearNote();
}

/** 인증 처리 중 오류로 멈췄을 때 — 쪽지는 남겨 이어 하기가 마저 처리하게 한다 */
export function pauseSignupFollowUp(): void {
    verifying = false;
}

/** 지금 이 화면에서 인증 확인을 처리 중인가 — 로그인 화면 경로 보호(PublicRoute)가 새 계정을 먼저 넘기지 않게 본다 */
export const isSignupFollowUpInFlight = (): boolean => verifying;

// 리퍼럴 코드 처리 함수 (2026-10-22 useAuthStore에서 옮김 — 동작 그대로)
export async function processReferralCode(userId: string) {
    try {
        // localStorage에서 리퍼럴 코드 확인
        const referralCode = localStorage.getItem('referral_code');

        if (!referralCode) {
            // 쿠키에서도 확인
            const cookies = document.cookie.split(';');
            for (const cookie of cookies) {
                const [name, value] = cookie.trim().split('=');
                if (name === 'referral_code') {
                    if (value && value.length === 8) {
                        await handleReferralSignup(value, userId);
                    }
                    break;
                }
            }
        } else if (referralCode.length === 8) {
            await handleReferralSignup(referralCode, userId);
        }
    } catch (error) {
        console.error('리퍼럴 코드 처리 중 오류:', error);
        // 리퍼럴 처리 실패는 회원가입 프로세스를 막지 않음
    }
}

// 리퍼럴 가입 처리
async function handleReferralSignup(referralCode: string, newUserId: string) {
    try {
        const { data, error } = await supabase.functions.invoke('process-referral-signup', {
            body: { referralCode, newUserId }
        });

        if (!error && data?.success) {
            console.log('✅ 리퍼럴 연결 성공');
            // 리퍼럴 코드 삭제
            localStorage.removeItem('referral_code');
            document.cookie = 'referral_code=; path=/; max-age=0';
        }
    } catch (error) {
        console.error('리퍼럴 가입 처리 실패:', error);
    }
}

// 회원가입 알림톡 발송 함수 (솔라피 API 사용, 2026-10-22 useAuthStore에서 옮김 — 동작 그대로)
export async function sendSignupAlimtalk(user: SupabaseUser, phone: string) {
    try {
        const { data, error } = await supabase.functions.invoke('zapier-webhook', {
            body: {
                user_id: user.id,
                phone: phone,
                email: user.email || null,
                created_at: user.created_at,
                event_type: 'user_signup'
            }
        });

        if (!error && data?.success) {
            console.log('✅ 회원가입 알림톡 발송 성공');
        } else {
            console.error('❌ 회원가입 알림톡 발송 실패:', error);
        }
    } catch (error) {
        console.error('💥 회원가입 알림톡 발송 중 오류:', error);
        // 알림톡 실패는 회원가입 프로세스를 막지 않음
    }
}

/**
 * 동의 증거(2026-09-16): 신규 가입자가 켠 "이용약관에 동의합니다" 체크박스를 시각·IP와 함께 기록. 실패해도 가입은 계속.
 * 동의 칸은 새 번호에게만 보이고 필수라, 칸을 체크했고 새 번호(또는 가입 화면·생성 30분 안)일 때만 남긴다. (2026-10-22 AuthPage에서 옮김)
 */
export async function recordSignupConsent(opts: { agreedToTerms: boolean; isSignUp: boolean; isExistingUser: boolean; isNewUser: boolean; resumed?: boolean }): Promise<void> {
    if (!(opts.agreedToTerms && (!opts.isExistingUser || opts.isSignUp || opts.isNewUser))) return;
    await recordTermsConsent({
        termsKey: 'signup_terms',
        // 2026-10-27: 동의 칸을 이용약관·개인정보 수집·이용·제3자 제공(VIP 검색자 알림) 세 칸(모두 필수)으로 나눔 — 고지 원문을 그대로 남긴다.
        text: `회원가입 화면에서 아래 세 필수 항목에 모두 체크한 뒤 휴대전화 인증을 완료함. [필수] 소셜데이터랩 서비스 이용약관(최종 업데이트 ${PRIVACY_POLICY_EFFECTIVE_DATE}, 회원가입 화면 모달) 동의. [필수] 개인정보 수집·이용 동의 — ${noticeAsText(COLLECTION_NOTICE)}. [필수] 개인정보 제3자 제공 동의(VIP 검색자 알림) — ${noticeAsText(VIP_THIRD_PARTY_NOTICE)}.`,
        meta: {
            is_sign_up: opts.isSignUp,
            is_new_user: opts.isNewUser,
            is_existing_phone: opts.isExistingUser,
            ...(opts.resumed ? { resumed_after_reload: true } : {}),
        },
    });
}

/** 가입 접속 정보(save-registration-data → user_registration_tracking, VIP '나를 분석한 사람 상세'가 읽음) — 이미 있으면 건너뜀. 실패해도 가입은 계속. (2026-10-22 AuthPage에서 옮김) */
export async function saveSignupRegistrationData(userId: string, isNewSignup: boolean): Promise<void> {
    try {
        // 기존 tracking 데이터가 있는지 확인
        const { data: existingTrackingData } = await supabase
            .from('user_registration_tracking')
            .select('id')
            .eq('user_id', userId)
            .limit(1);

        const hasExistingData = existingTrackingData && existingTrackingData.length > 0;
        console.log('📊 기존 추적 데이터 여부:', hasExistingData);

        // 신규 사용자이고 추적 데이터가 없으면 데이터 수집
        if (isNewSignup && !hasExistingData) {
            console.log('📊 회원가입 데이터 수집 시작:', userId);

            const clientData = collectBrowserData();
            console.log('📋 클라이언트 데이터 수집 완료:', Object.keys(clientData));

            // Supabase Edge Function 호출하여 데이터 저장
            const response = await supabase.functions.invoke('save-registration-data', {
                body: {
                    userId,
                    clientData: clientData
                }
            });

            if (response.error) {
                console.error('❌ 등록 데이터 수집 실패:', response.error);
            } else {
                console.log('✅ 등록 데이터 수집 완료:', response.data);
            }
        } else {
            console.log('👤 기존 사용자 또는 이미 데이터 수집됨 - 생략');
        }
    } catch (trackingError) {
        console.error('💥 등록 데이터 수집 중 오류:', trackingError);
        // 데이터 수집 실패해도 가입은 계속 진행
    }
}

// 브라우저 정보 수집 함수 (2026-10-22 AuthPage에서 옮김 — 동작 그대로)
const collectBrowserData = (): any => {
    const clientData: any = {
        // 브라우저 정보
        userAgent: navigator.userAgent,
        browserLanguage: navigator.language,
        systemLanguage: navigator.language,
        preferredLanguages: Array.from(navigator.languages || []),

        // 화면 정보
        screenWidth: window.screen.width,
        screenHeight: window.screen.height,
        screenResolution: `${window.screen.width}x${window.screen.height}`,
        colorDepth: window.screen.colorDepth,
        pixelRatio: window.devicePixelRatio,

        // 시간 정보
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        localTime: new Date().toISOString(),
        utcOffset: new Date().getTimezoneOffset(),
        dstActive: (new Date().getTimezoneOffset() !== new Date(new Date().getFullYear(), 0, 1).getTimezoneOffset()),

        // 브라우저 기능
        cookiesEnabled: navigator.cookieEnabled,
        touchSupport: 'ontouchstart' in window || navigator.maxTouchPoints > 0,
        doNotTrack: navigator.doNotTrack === '1',

        // 참조 정보
        referrerUrl: document.referrer,

        // 세션 ID 생성
        sessionId: crypto.randomUUID ? crypto.randomUUID() : Date.now().toString(36) + Math.random().toString(36).substr(2)
    };

    // 네트워크 정보 (지원되는 경우)
    if ('connection' in navigator) {
        const connection = (navigator as any).connection;
        clientData.connectionType = connection.effectiveType;
        clientData.connectionDownlink = connection.downlink;
        clientData.connectionRtt = connection.rtt;
        clientData.connectionSaveData = connection.saveData;
    }

    // 하드웨어 정보 (지원되는 경우)
    if ('hardwareConcurrency' in navigator) {
        clientData.cpuCores = navigator.hardwareConcurrency;
    }

    // 메모리 정보 (지원되는 경우)
    if ('deviceMemory' in navigator) {
        clientData.memoryGb = (navigator as any).deviceMemory;
    }

    // WebGL 지원 확인
    try {
        const canvas = document.createElement('canvas');
        const gl = canvas.getContext('webgl') || canvas.getContext('experimental-webgl');
        clientData.webglSupported = !!gl;
    } catch (e) {
        clientData.webglSupported = false;
    }

    // 광고 차단기 감지 (간단한 방법)
    const testAd = document.createElement('div');
    testAd.innerHTML = '&nbsp;';
    testAd.className = 'adsbox';
    testAd.style.position = 'absolute';
    testAd.style.left = '-10000px';
    document.body.appendChild(testAd);
    setTimeout(() => {
        clientData.adBlockerDetected = testAd.offsetHeight === 0;
        document.body.removeChild(testAd);
    }, 100);

    return clientData;
};

/**
 * 앱이 켜질 때(로그인 확인 뒤) — 쪽지가 남아 있으면 남은 단계만 마저 한다. 같은 화면에서 인증이 진행 중이면 건너뛴다.
 * 다른 번호의 로그인, 30분 지난 쪽지, 새 계정이 아닌 손님(기존 회원 로그인)은 할 일이 없으므로 쪽지만 지운다.
 */
export function resumeSignupFollowUp(user: SupabaseUser): Promise<void> {
    if (verifying) return Promise.resolve();
    if (!resuming) {
        resuming = runResume(user).finally(() => {
            resuming = null;
        });
    }
    return resuming;
}

async function runResume(user: SupabaseUser): Promise<void> {
    const note = readNote();
    if (!note) return;
    if (note.phoneKey !== toPhoneKey(user.phone) || Date.now() - note.at > FRESH_ACCOUNT_MS || !isFreshAccount(user.created_at)) {
        clearNote();
        return;
    }
    console.log('🧾 가입 뒤처리 이어 하기 시작 — 끝난 단계:', Object.keys(note.done));
    const pending: Promise<void>[] = [];
    if (!note.done.referral) pending.push(processReferralCode(user.id).then(() => markSignupStepDone('referral')));
    if (!note.done.alimtalk) pending.push(sendSignupAlimtalk(user, note.phone).then(() => markSignupStepDone('alimtalk')));
    if (!note.done.consent) {
        pending.push(recordSignupConsent({
            agreedToTerms: note.agreedToTerms,
            isSignUp: note.isSignUp,
            isExistingUser: note.isExistingUser,
            isNewUser: true,
            resumed: true,
        }).then(() => markSignupStepDone('consent')));
    }
    if (!note.done.tracking) pending.push(saveSignupRegistrationData(user.id, true).then(() => markSignupStepDone('tracking')));
    await Promise.all(pending);
    clearNote();
    console.log('✅ 가입 뒤처리 이어 하기 완료');
}
